<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ZapiAPI from 'dashboard/api/zapi';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      idZapi: '',
      tokenZapi: '',
      clientZapi: '',
      deviceInfo: null,
      isFetchingDevice: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    bridgeZapiUrl() {
      return (window.chatwootConfig?.bridgeZapiUrl || '').replace(/^https?:\/\//, '');
    },
    webhookUrl() {
      if (!this.idZapi || !this.tokenZapi || !this.clientZapi) return '';
      return `https://${this.bridgeZapiUrl}/webhook/zapi/receive`;
    },
    canFetchDevice() {
      return this.idZapi && this.tokenZapi && this.clientZapi;
    },
  },
  validations: {
    channelName: { required },
    idZapi: { required },
    tokenZapi: { required },
    clientZapi: { required },
  },
  methods: {
    async fetchDeviceInfo() {
      if (!this.canFetchDevice) return;

      this.isFetchingDevice = true;
      try {
        const { data } = await ZapiAPI.getDevice({
          idZapi: this.idZapi,
          tokenZapi: this.tokenZapi,
          clientZapi: this.clientZapi,
        });
        this.deviceInfo = data;
        if (data.name && !this.channelName) {
          this.channelName = data.name;
        }
      } catch {
        useAlert(
          this.$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.DEVICE.ERROR')
        );
        this.deviceInfo = null;
      } finally {
        this.isFetchingDevice = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const zapiChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName?.trim(),
            channel: {
              type: 'api',
              webhook_url: this.webhookUrl,
              additional_attributes: {
                id_zapi: this.idZapi,
                token_zapi: this.tokenZapi,
                client_zapi: this.clientZapi,
                channel_subtype: 'zapi_whatsapp',
              },
            },
          }
        );

        // Register webhook on Z-api side
        try {
          await ZapiAPI.updateWebhook(zapiChannel.id);
        } catch {
          // Channel was created, webhook registration failed — user can retry from settings
        }

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: zapiChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.idZapi.$error }">
          {{ $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.ID_ZAPI.LABEL') }}
          <input
            v-model="idZapi"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.ID_ZAPI.PLACEHOLDER')
            "
            @blur="v$.idZapi.$touch"
          />
          <span v-if="v$.idZapi.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.ID_ZAPI.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.tokenZapi.$error }">
          {{ $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.TOKEN_ZAPI.LABEL') }}
          <input
            v-model="tokenZapi"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.TOKEN_ZAPI.PLACEHOLDER'
              )
            "
            @blur="v$.tokenZapi.$touch"
          />
          <span v-if="v$.tokenZapi.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.TOKEN_ZAPI.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.clientZapi.$error }">
          {{ $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CLIENT_ZAPI.LABEL') }}
          <input
            v-model="clientZapi"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CLIENT_ZAPI.PLACEHOLDER'
              )
            "
            @blur="v$.clientZapi.$touch"
          />
          <span v-if="v$.clientZapi.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CLIENT_ZAPI.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2 mb-4">
        <NextButton
          :is-loading="isFetchingDevice"
          :disabled="!canFetchDevice"
          type="button"
          solid
          slate
          :label="$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.DEVICE.FETCH_BUTTON')"
          @click="fetchDeviceInfo"
        />
      </div>

      <div
        v-if="deviceInfo"
        class="flex items-center gap-4 p-4 mb-4 rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-25 dark:bg-slate-800"
      >
        <img
          v-if="deviceInfo.imgUrl"
          :src="deviceInfo.imgUrl"
          :alt="deviceInfo.name"
          class="w-16 h-16 rounded-full object-cover"
        />
        <div
          v-else
          class="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400"
        >
          <span class="text-2xl">📱</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ deviceInfo.name || '—' }}
          </span>
          <span
            v-if="deviceInfo.bio"
            class="text-xs text-slate-500 dark:text-slate-400 mt-1"
          >
            {{ deviceInfo.bio }}
          </span>
        </div>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CHANNEL_NAME.PLACEHOLDER'
              )
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div v-if="webhookUrl" class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.WEBHOOK_URL.LABEL') }}
          <input
            :value="webhookUrl"
            type="text"
            readonly
            class="bg-slate-50 dark:bg-slate-800"
          />
        </label>
        <p class="help-text">
          {{
            $t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.WEBHOOK_URL.SUBTITLE')
          }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.ZAPI_WHATSAPP_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
